import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageTitle } from "@/pages/admin/AdminLayout";
import { api, formatErr } from "@/lib/api";

const EMPTY = { name: "", region: "", contact_name: "", contact_phone: "", contact_email: "", capacity: "", notes: "" };
const FIELDS = [["name", "Farm name"], ["region", "Region"], ["contact_name", "Contact name"], ["contact_phone", "Contact phone"], ["contact_email", "Contact email"], ["capacity", "Capacity (e.g. 200 lbs/month)"]];

const FarmDialog = ({ farm, onClose, onSaved }) => {
  const [form, setForm] = useState(farm || EMPTY);
  useEffect(() => {
    setForm(farm || EMPTY);
  }, [farm]);
  const save = async () => {
    try {
      if (form.id) await api.put(`/admin/farms/${form.id}`, form);
      else await api.post("/admin/farms", form);
      toast.success("Farm saved");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <Dialog open={!!farm} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="farm-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit farm" : "Add farm"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, l]) => <Input key={k} placeholder={l} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-11 rounded-xl" data-testid={`farm-${k}-input`} />)}
          <Textarea placeholder="Private notes" rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl sm:col-span-2" data-testid="farm-notes-input" />
        </div>
        <button onClick={save} className="h-11 rounded-full bg-primary text-primary-foreground font-bold" data-testid="farm-save-button">Save farm</button>
      </DialogContent>
    </Dialog>
  );
};

export default function Farms() {
  const [farms, setFarms] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/farms").then((r) => setFarms(r.data)).catch((e) => toast.error(formatErr(e))), []);
  useEffect(() => {
    load();
  }, [load]);
  const del = async (id) => {
    if (!window.confirm("Delete this farm?")) return;
    await api.delete(`/admin/farms/${id}`);
    load();
  };
  return (
    <div data-testid="admin-farms-page">
      <PageTitle title="Farm partners" sub="Private and admin-only. Never shown to the public or applicants.">
        <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex h-10 items-center gap-2 px-5 rounded-full bg-primary text-primary-foreground font-bold text-sm" data-testid="add-farm-button"><Plus className="h-4 w-4" />Add farm</button>
      </PageTitle>
      <FarmDialog farm={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      {farms.length === 0 && <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="farms-empty"><Lock className="h-6 w-6 mx-auto mb-2" />No farms yet. Add your first partner farm.</div>}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {farms.map((f) => (
          <div key={f.id} className="rounded-2xl border border-border bg-card p-5 hover:border-primary/60 transition-colors" data-testid={`farm-card-${f.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg font-bold">{f.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{f.region || "-"}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(f)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-secondary" data-testid={`farm-edit-${f.id}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(f.id)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-secondary" data-testid={`farm-delete-${f.id}`} aria-label="Delete"><Trash2 className="h-4 w-4 text-destructive" /></button>
              </div>
            </div>
            <dl className="mt-4 text-sm space-y-1">
              <p><span className="text-muted-foreground">Contact:</span> {f.contact_name || "-"} {f.contact_phone && `· ${f.contact_phone}`}</p>
              <p><span className="text-muted-foreground">Email:</span> {f.contact_email || "-"}</p>
              <p><span className="text-muted-foreground">Capacity:</span> {f.capacity || "-"}</p>
              <p><span className="text-muted-foreground">Intros made:</span> <span className="font-bold">{f.intro_count}</span></p>
              {f.notes && <p className="text-muted-foreground whitespace-pre-wrap pt-2">{f.notes}</p>}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}
